import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogBackdrop,
	DialogPanel,
	DialogTitle,
	Transition,
} from "@headlessui/react";
import { FiX } from "react-icons/fi";
import {
	fetchCampaignPosts,
	saveCampaignPost,
} from "../../services/CampaignService";
import { format } from "date-fns";
import { Button } from "../../components/Button";

interface CampainPostsProps {
	open: boolean;
	onClose: () => void;
	campainId: string | number;
	onNewUpdate?: () => void;
}

const CampainPosts: React.FC<CampainPostsProps> = ({
	open,
	onClose,
	campainId,
	onNewUpdate,
}) => {
	const [showInput, setShowInput] = useState(false);
	const [message, setMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [updateList, setUpdateList] = useState<any[]>([]);
	const [loadingPosts, setLoadingPosts] = useState(false);

	useEffect(() => {
		if (open && campainId) {
			setLoadingPosts(true);
			fetchCampaignPosts(String(campainId))
				.then((res) => {
					if (res && Array.isArray(res.data)) {
						setUpdateList(res.data);
					} else if (res && res.data) {
						setUpdateList([res.data]);
					} else {
						setUpdateList([]);
					}
				})
				.catch(() => setUpdateList([]))
				.finally(() => setLoadingPosts(false));
		}
	}, [open, campainId]);

	const handleNewUpdate = () => {
		setShowInput(true);
		if (onNewUpdate) onNewUpdate();
	};

	const handleCancel = () => {
		setShowInput(false);
		setMessage("");
	};

	const handlePostUpdate = async () => {
		if (!message.trim()) return;
		setLoading(true);
		try {
			const postBody = {
				content: message,
				campaignId: campainId,
				date: new Date().toISOString(),
			};
			await saveCampaignPost(postBody);
			const res = await fetchCampaignPosts(String(campainId));
			if (res && Array.isArray(res.data)) {
				setUpdateList(res.data);
			} else if (res && res.data) {
				setUpdateList([res.data]);
			}
			setShowInput(false);
			setMessage("");
		} finally {
			setLoading(false);
		}
	};

	return (
		<Transition show={open} as={React.Fragment}>
			<Dialog
				as="div"
				className="fixed inset-0 z-50 overflow-y-auto"
				open={open}
				onClose={onClose}
			>
				<DialogBackdrop className="fixed inset-0 bg-black bg-opacity-30" />
				<div className="min-h-screen px-4 flex items-start justify-center mt-16">
					<DialogPanel className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl mx-auto">
						<div className="flex items-center justify-between px-6 py-4 border-b">
							<DialogTitle as="h3" className="text-lg font-semibold text-gray-900">
								Updates
							</DialogTitle>
							<button
								onClick={onClose}
								className="text-gray-500 hover:text-gray-700 p-1 rounded"
								aria-label="Close"
								type="button"
							>
								<FiX size={22} />
							</button>
						</div>
						<div className="px-6 pt-4 pb-6">
							{!showInput ? (
								<>
									<div className="flex justify-end mb-4">
										<Button
											variant="primary"
											size="md"
											onClick={handleNewUpdate}
										>
											New update
										</Button>
									</div>
									{loadingPosts ? (
										<div className="text-center text-gray-500 py-8">
											Loading...
										</div>
									) : (
											<div
												className="space-y-4"
												style={{ maxHeight: "400px", overflowY: "auto" }}
											>
												{updateList.length === 0 ? (
													<div className="text-center text-gray-400">
														No updates found.
													</div>
												) : (
													updateList.map((update) => (
														<div
															key={update.id}
															className="border rounded p-4 flex flex-col gap-2"
														>
															<div className="flex items-center gap-3">
																<div className="flex-shrink-0 w-10 h-10 rounded-full bg-warning flex items-center justify-center font-bold text-lg text-white">
																	{update.user.name.charAt(0) || "U"}
																</div>
																<div>
																	<div className="font-semibold text-gray-900 text-base">
																		{update.user.name || "Unknown"}
																	</div>
																	<div className="text-xs text-gray-500">
																		{update.role || ""}
																	</div>
																</div>
																<div className="flex-1" />
																<div className="text-xs text-gray-500">
																	{update.date
																		? format(
																				new Date(update.date),
																				"MM/dd/yyyy h:mm a",
																			)
																		: ""}
																</div>
															</div>
															<div className="ml-12 mt-1 text-gray-800">
																{update.content || ""}
															</div>
														</div>
													))
												)}
											</div>
										)}
								</>
							) : (
								<form
									className="space-y-6"
									onSubmit={(e) => {
										e.preventDefault();
										handlePostUpdate();
									}}
								>
									<div>
										<label className="block font-medium mb-2">Message</label>
										<textarea
											className="w-full border rounded px-3 py-2 min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-primary-100"
											placeholder="Type here..."
											value={message}
											onChange={(e) => setMessage(e.target.value)}
											disabled={loading}
										/>
									</div>
									<div className="flex justify-end gap-3">
										<Button
											variant="secondary"
											size="md"
											onClick={handleCancel}
											disabled={loading}
										>
											Cancel
										</Button>
										<Button
											variant="primary"
											size="md"
											type="submit"
											disabled={loading}
										>
											{loading ? "Posting..." : "Post update"}
										</Button>
									</div>
								</form>
							)}
						</div>
					</DialogPanel>
				</div>
			</Dialog>
		</Transition>
	);
};

export default CampainPosts;
